import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from '@src/design-system/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@src/design-system/tabs';

import { Formik } from 'formik';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { Button } from '@src/design-system/button';

import leadApi, {
  importLeads,
  type TEditLeadBody,
} from '@features/leads/services/lead-api';

import {
  createLeadInitialValues,
  leadFormSchema,
  importLeadsFormSchema,
  type TSingleLeadFormValues,
  type TLeadFormValues,
} from '../helper';
import type { TLead } from '@shared/types/lead';
import { CreateLeadForm } from '../components/create-lead-form';
import ImportLeadsForm from '../components/import-leads-form';
import { useDialogContext } from '@src/design-system/dialog/use-dialog-context';

export const CreateLeadModal: FC<{ lead?: TLead }> = ({ lead }) => {
  const [reset, setReset] = useState<() => void>();
  const [currentTab, setCurrentTab] = useState<'single' | 'import'>('single');
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { mutate: importLeadsFromFile } = importLeads.useCreate();
  const { mutate: createLead, isPending: createLeadPending } =
    leadApi.useCreate();
  const { mutate: updateLead, isPending: updateLeadPending } =
    leadApi.useUpdate();

  const handleCreateLead = (values: TLeadFormValues) => {
    if (currentTab === 'import') {
      importLeadsFromFile(
        {
          file: values.file as File,
          programs: values.programs?.map((program) => program.id),
        },
        {
          onSuccess: () => {
            buttonRef.current?.click();
          },
        }
      );
      return;
    }

    const data = {
      ...values,
      ...(currentTab === 'single' && {
        assignee: (values as any).assignee?.id,
        referal: (values as any).referal?.id,
        labels:
          (values as any).labels?.map((l: any) => ('id' in l ? l.id : l)) || [],
      }),
      programs: values.programs?.map((program) => program.id),
    };

    const onSuccess = () => {
      if (reset) return reset();
      buttonRef.current?.click();
    };

    if (lead) {
      updateLead(
        {
          id: +lead?.id,
          ...data,
        },
        { onSuccess }
      );
    } else {
      createLead(data as unknown as TEditLeadBody, {
        onSuccess,
      });
    }
  };

  const { formikRef } = useDialogContext();

  return (
    <Formik
      validationSchema={
        currentTab === 'import' ? importLeadsFormSchema : leadFormSchema
      }
      innerRef={formikRef}
      initialValues={createLeadInitialValues({ lead, tab: currentTab })}
      key={currentTab}
      onSubmit={handleCreateLead}
    >
      {({ values, setFieldValue, submitForm, resetForm }) => {
        return (
          <>
            <DialogHeader>Add New Lead</DialogHeader>
            <DialogBody>
              {lead ? (
                <CreateLeadForm
                  values={values as TSingleLeadFormValues}
                  setFieldValue={setFieldValue}
                />
              ) : (
                <Tabs
                  value={currentTab}
                  onValueChange={(value) =>
                    setCurrentTab(value as 'single' | 'import')
                  }
                >
                  <TabsList className=' w-full mb-4'>
                    <TabsTrigger value='single'>Single lead</TabsTrigger>
                    <TabsTrigger value='import'>Import leads</TabsTrigger>
                  </TabsList>
                  <TabsContent value='single'>
                    <CreateLeadForm
                      values={values as TSingleLeadFormValues}
                      setFieldValue={setFieldValue}
                    />
                  </TabsContent>
                  <TabsContent value='import'>
                    <ImportLeadsForm
                      values={values as any}
                      setFieldValue={setFieldValue}
                    />
                  </TabsContent>
                </Tabs>
              )}
            </DialogBody>

            <DialogFooter className='mt-auto'>
              <Button
                className='flex-1'
                type='button'
                disabled={createLeadPending || updateLeadPending}
                onClick={() => {
                  setReset(undefined);
                  submitForm();
                }}
              >
                Save & Close
              </Button>
              <Button
                className='flex-1'
                type='button'
                variant='primaryOutline'
                disabled={createLeadPending || updateLeadPending}
                onClick={() => {
                  setReset(() => resetForm);
                  submitForm();
                }}
              >
                Save & add Another
              </Button>
              <DialogClose asChild>
                <Button
                  className='w-3/6 hidden'
                  variant='primaryOutline'
                  ref={buttonRef}
                >
                  Close
                </Button>
              </DialogClose>
            </DialogFooter>
          </>
        );
      }}
    </Formik>
  );
};
